import * as joint from "@clientio/rappid";
import { dia } from "@clientio/rappid";
import { UIEventBus } from "../../event-bus";
import { DirectEdit } from "../mvc/base/direct-edit";
import RappidContext from "./rappid-context";
import { TransitionView } from "../mvc/transition";
import { deselectAll } from "../mvc/base/utils";
import { isInActiveSubdiagram } from "../mvc/base/element-controller";
import { activeView } from "../subdiagram-viewer";

export const SUBDIAGRAM_FILTER = (cell: joint.dia.Cell) => {
  if (cell.attributes?.type === "Statechart" || cell.attributes?.type === "NodeLabel") {
    return true;
  }

  return cell.attr("root/display") === "none";
};

export default class SelectionController {
  private highlighted = false;
  private targetView: dia.ElementView = null;

  constructor(private context: RappidContext) {}

  activate() {
    this.activateSelection();
  }

  activateSelection() {
    const selection = this.context.selection;
    selection.collection.on("reset add remove", () => this.onSelectionChange());
    selection.collection.on("reset", (collection, ctx) => {
      this.blurDirectEditViews(ctx.previousModels);
    });

    // Initiate selecting when the user grabs the blank area of the paper while the Shift key is pressed.
    // Otherwise, initiate paper pan.
    this.context.paper.on("blank:pointerdown", (evt: joint.dia.Event) => {
      if (evt.shiftKey) {
        selection.options.filter = SUBDIAGRAM_FILTER;
        selection.startSelecting(evt);
      } else {
        selection.collection.reset([]);
        this.context.paperScroller.startPanning(evt);
      }
      this.removeTransitionTools();
    });

    this.context.paper.on("cell:pointerdown", (cellView: joint.dia.CellView, evt: joint.dia.Event) => {
      //Middle mouse wheel is pressed -> just pan without selecting
      if (evt.button === 1) {
        this.middleMousePan(cellView, evt);
      }
      // Rubber Band Selection within a composite State
      if (evt.shiftKey) {
        cellView.preventDefaultInteraction(evt);
        selection.options.filter = (cell) => !cell.isEmbeddedIn(cellView.model) || SUBDIAGRAM_FILTER(cell);
        selection.startSelecting(evt);
        // Select an element if CTRL/Meta key is pressed while the element is clicked.
      } else if (evt.metaKey || evt.ctrlKey) {
        selection.collection.add(cellView.model);
      } else {
        if (evt.target.getAttribute("data-action") === "fork") {
          // do not select forked states which breaks undo/redo functionality (#71)
          return;
        }
        if (selection.collection.models.indexOf(cellView.model) >= 0) {
          // If the element is currently selected do nothing to avoid embedded editor blur
          return;
        }
        selection.collection.reset([]);
        selection.collection.add(cellView.model);
      }
    });

    selection.on(
      "selection-box:pointerdown",
      (elementView: joint.dia.ElementView, evt: joint.dia.Event) => {
        this.context.commandManager.initBatchCommand();

        if (evt.button === 1) {
          this.middleMousePan(elementView, evt);
        }
        // Unselect an element if the CTRL/Meta key is pressed while a selected element is clicked.
        if (evt.metaKey || evt.ctrlKey) {
          selection.collection.remove(elementView.model);
        }
        this.highlighted = false;
        this.targetView = null;
      },
      this
    );

    selection.on(
      "selection-box:pointermove",
      (elementView: joint.dia.ElementView, evt: joint.dia.Event) => {
        selection.collection.models.forEach((m) => {
          if (!m.isEmbedded()) {
            const parent = m.getParentCell();
            if (!selection.collection.get(parent)) {
              parent.unembed(m);
            }
          }
        });

        const point = this.context.paper.clientToLocalPoint({
          x: evt.clientX,
          y: evt.clientY,
        });
        const views = this.context.paper.findElementViewsAtPoint(point).filter((v) => {
          const allInside = selection.collection.models.every((model) => {
            const modelView = this.context.paper.findViewByModel(model);
            if (!modelView) return false;
            const modelBBox = modelView.getBBox();
            return v.getBBox().containsRect(modelBBox);
          });
          return allInside;
        });

        const newTargetView = [...views].reverse().find((v) => v.model.canEmbed(elementView.model));

        if (this.targetView && this.targetView !== newTargetView) {
          this.targetView.unhighlight(this.targetView.el, { embedding: true });
          this.highlighted = false;
        }

        if (!newTargetView) {
          this.targetView = null;
          return;
        }

        if (!this.highlighted || this.targetView !== newTargetView) {
          newTargetView.highlight(newTargetView.el, { embedding: true });
          this.highlighted = true;
        }

        this.targetView = newTargetView;
      },
      this
    );

    selection.on(
      "selection-box:pointerup",
      () => {
        this.context.commandManager.storeBatchCommand();

        if (!this.targetView) {
          if (activeView.value !== null) {
            this.context.commandManager.undo();
            this.highlighted = false;
            this.targetView = null;
          }
          return;
        }
        this.targetView.unhighlight(this.targetView.el, {
          embedding: true,
        });
        selection.collection.models.forEach((m) => {
          if (this.targetView.model.canEmbed(m)) {
            this.targetView.model.embed(m);
          }
        });
        this.highlighted = false;
        this.targetView = null;
      },
      this
    );
  }

  onSelectionChange() {
    const { paper, selection } = this.context;
    const { collection } = selection;
    this.removeTransitionTools();
    deselectAll(this.context, false);
    if (collection.length === 1) {
      const primaryCell: joint.dia.Cell = collection.first();
      selection.destroySelectionBox(primaryCell);
    } else if (collection.length === 2) {
      collection.each(function (cell: joint.dia.Cell) {
        selection.createSelectionBox(cell);
      });
    }
    UIEventBus.emit("cellSelectionChanged", collection);
    (document.activeElement as HTMLElement).blur();
  }

  blurDirectEditViews(models: dia.Cell[]) {
    this.getDirectEditViews(models).forEach((v) => v.setFocus(false));
  }

  getDirectEditViews(models: dia.Cell[]): DirectEdit[] {
    return models
      .map((c) => this.context.paper.findViewByModel(c))
      .filter((v) => v !== undefined)
      .filter((v) => "setFocus" in v)
      .map((v) => v as unknown as DirectEdit);
  }

  private removeTransitionTools() {
    this.context.paper.model.getLinks().forEach((transition) => {
      const view = transition.findView(this.context.paper);
      if (view instanceof TransitionView) {
        view.removeTools();
      }
    });
  }

  private middleMousePan(cellView: joint.dia.CellView, evt: joint.dia.Event) {
    this.context.selection.collection.reset([]);
    this.context.paperScroller.startPanning(evt);
    this.removeTransitionTools();
    cellView.preventDefaultInteraction(evt);
    evt.preventDefault();
  }
}
